import { useTranslation } from 'react-i18next';
import { CrispChat } from '@/components/chat/CrispChat';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { ThemeSwitcher } from '@/components/ui/theme-switcher';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { 
  Bot, CheckSquare, Users, FileCheck, BarChart3, 
  Check, Star, ArrowRight, Sparkles, Target, Shield, Zap,
  Clock, Globe, Lock, LineChart, Workflow, Bell,
  Building2, Rocket, Award, TrendingUp, Play,
  ChevronRight, MousePointer2, Layers, ArrowUpRight
} from 'lucide-react';

// Testimonials
const testimonials = [
  { 
    name: 'Sarah Chen', 
    role: 'COO, TechStart (50+ employees)', 
    quote: 'Reduced our operational overhead by 40%. The approval workflows alone saved us 10 hours per week.', 
    avatar: 'SC',
    metric: '40% less overhead'
  },
  { 
    name: 'Ahmed Hassan', 
    role: 'Operations Director, FinServ Inc', 
    quote: 'Finally, a tool that understands how we work. Multi-language support was crucial for our global team.', 
    avatar: 'AH',
    metric: '3 offices connected'
  },
  { 
    name: 'Priya Sharma', 
    role: 'Team Lead, GrowthOps', 
    quote: 'My team loves it. Clear task tracking, smart workload alerts, and everything just works seamlessly.', 
    avatar: 'PS',
    metric: '2x faster approvals'
  },
];

const stats = [
  { value: '500+', label: 'Teams using OpsGPT', icon: Users },
  { value: '40%', label: 'Average time saved', icon: Clock },
  { value: '99.9%', label: 'Uptime SLA', icon: Shield },
  { value: '4.8/5', label: 'Customer rating', icon: Star },
];

// Stagger animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const scaleInVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

export default function Landing() {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const features = [
    { icon: Target, title: 'Operations Dashboard', desc: 'See tasks, approvals, and team workload in one unified view. Real-time updates, no refresh needed.', color: 'from-primary/20 to-accent/20' },
    { icon: CheckSquare, title: 'Smart Task Management', desc: 'Kanban boards, time tracking, dependencies, and templates. Organize work your way.', color: 'from-success/20 to-info/20' },
    { icon: Users, title: 'Team Coordination', desc: 'Workload balancing, capacity planning, and skill-based assignment. Know who\'s doing what.', color: 'from-warning/20 to-destructive/20' },
    { icon: FileCheck, title: 'Approval Workflows', desc: 'Streamlined approvals for budgets, expenses, and requests. Track status in real-time.', color: 'from-info/20 to-primary/20' },
    { icon: BarChart3, title: 'Analytics & Insights', desc: 'Track productivity, identify bottlenecks, and export reports. Data-driven decisions.', color: 'from-accent/20 to-success/20' },
    { icon: Bot, title: 'Optional AI Assistant', desc: 'AI-powered suggestions for task delegation and risk alerts. Works great without it too.', color: 'from-primary/20 to-warning/20' },
  ];

  const workflowSteps = [
    { step: '01', title: 'Sign Up & Setup', desc: 'Create your org in under 2 minutes. Invite your team instantly.', icon: Rocket },
    { step: '02', title: 'Organize Work', desc: 'Create tasks, set up approval flows, assign roles & permissions.', icon: Layers },
    { step: '03', title: 'Execute & Track', desc: 'Your team works. OpsGPT tracks everything with real-time dashboards.', icon: TrendingUp },
    { step: '04', title: 'Optimize & Scale', desc: 'Use analytics to find bottlenecks. AI suggests improvements.', icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEOHead
        title="OpsGPT – AI-Powered Operations & Approvals for Teams"
        description="Execute decisions, not manage chaos. OpsGPT helps teams track tasks, manage approvals, and monitor workload with optional AI intelligence. Free 14-day trial."
        canonical="https://opsgpt.app"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "OpsGPT",
          "url": "https://opsgpt.app",
          "description": "AI-powered business operations control system for teams",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://opsgpt.app/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <CrispChat websiteId="94075e70-9e78-4d5d-9cf9-da865ebed9b8" />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/60 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg gradient-primary flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold">OpsGPT</span>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">Beta</Badge>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors relative group">
              Features
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full" />
            </a>
            <a href="#how-it-works" className="text-muted-foreground hover:text-foreground transition-colors relative group">
              How it Works
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full" />
            </a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors relative group">
              Pricing
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full" />
            </a>
            <Link to="/support" className="text-muted-foreground hover:text-foreground transition-colors relative group">
              Support
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full" />
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <Link to="/auth">
              <Button className="gap-2 shadow-glow-sm">{t('auth.signIn')} <ArrowUpRight className="h-3 w-3" /></Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero - Premium with parallax */}
        <section ref={heroRef} className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Subtle animated background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              className="absolute top-1/3 left-1/3 w-[600px] h-[600px] rounded-full opacity-[0.07] blur-[150px]"
              style={{ background: 'hsl(var(--primary))' }}
              animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full opacity-[0.05] blur-[130px]"
              style={{ background: 'hsl(200, 95%, 55%)' }}
              animate={{ x: [0, -25, 0], y: [0, 25, 0] }}
              transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            />
          </div>

          {/* Subtle dot pattern overlay */}
          <div className="absolute inset-0 opacity-[0.025]" style={{
            backgroundImage: `radial-gradient(circle, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }} />

          <motion.div 
            style={{ y: heroY, opacity: heroOpacity }}
            className="container mx-auto px-4 text-center relative z-10"
          >
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants}>
                <Badge className="mb-6 gap-2 px-4 py-2 glass text-sm" variant="outline">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                  </span>
                  Now in Public Beta — Free 14-day Pro Trial
                </Badge>
              </motion.div>

              <motion.h1 
                variants={itemVariants}
                className="mb-6 text-5xl font-bold tracking-tight sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]"
              >
                Execute Decisions,
                <br />
                <span className="text-primary">Not Manage Chaos</span>
              </motion.h1>

              <motion.p variants={itemVariants} className="mx-auto mb-4 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed">
                OpsGPT helps teams track work, approvals, and risks — with optional AI intelligence.
              </motion.p>

              <motion.div variants={itemVariants} className="mx-auto mb-8 max-w-xl">
                <span className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full text-sm">
                  <Shield className="h-4 w-4 text-primary" />
                  <strong className="text-foreground">This is NOT a CRM.</strong>
                  <span className="text-muted-foreground">OpsGPT handles execution after decisions are made.</span>
                </span>
              </motion.div>

              <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
                <Link to="/auth">
                  <Button size="lg" className="gap-2 text-base px-8 h-12 shadow-glow-sm hover:shadow-glow transition-shadow">
                    Start Free Trial <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <a href="#demo">
                  <Button size="lg" variant="outline" className="gap-2 text-base px-8 h-12 glass">
                    <Play className="h-4 w-4" /> See Features
                  </Button>
                </a>
              </motion.div>

              <motion.p variants={itemVariants} className="mt-6 text-xs text-muted-foreground flex items-center justify-center gap-4">
                <span className="flex items-center gap-1"><Check className="h-3 w-3 text-success" /> No credit card</span>
                <span className="flex items-center gap-1"><Check className="h-3 w-3 text-success" /> 14-day Pro trial</span>
                <span className="flex items-center gap-1"><Check className="h-3 w-3 text-success" /> Cancel anytime</span>
              </motion.p>
            </motion.div>

            {/* Scroll indicator */}
            <motion.div 
              className="absolute bottom-8 left-1/2 -translate-x-1/2"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <MousePointer2 className="h-5 w-5 text-muted-foreground/50" />
            </motion.div>
          </motion.div>
        </section>

        {/* Social Proof Stats - Floating cards */}
        <section className="relative z-10 -mt-12 pb-12">
          <div className="container mx-auto px-4">
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
            >
              {stats.map((stat, i) => (
                <motion.div key={i} variants={scaleInVariants}>
                  <Card className="glass-card text-center p-6 hover:shadow-glow-sm transition-all duration-300 hover:-translate-y-1 group">
                    <stat.icon className="h-5 w-5 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                    <div className="text-3xl font-bold text-foreground">{stat.value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* What OpsGPT Is / Isn't - Glass cards */}
        <section className="container mx-auto px-4 py-20">
          <motion.div 
            className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div variants={itemVariants}>
              <Card className="glass-card border-success/30 h-full group hover:border-success/60 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-success">
                    <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center">
                      <Check className="h-4 w-4" />
                    </div>
                    What OpsGPT IS
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-sm">
                    {['Operations control dashboard', 'Task & approval workflow system', 'Team workload & capacity tracker', 'Risk & deadline monitoring', 'Audit trail & activity logs'].map((item, i) => (
                      <motion.li key={i} className="flex items-center gap-3" initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} viewport={{ once: true }}>
                        <Check className="h-4 w-4 text-success flex-shrink-0" />{item}
                      </motion.li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div variants={itemVariants}>
              <Card className="glass-card border-destructive/30 h-full group hover:border-destructive/60 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-destructive">
                    <div className="h-8 w-8 rounded-full bg-destructive/20 flex items-center justify-center">
                      <Zap className="h-4 w-4" />
                    </div>
                    What OpsGPT is NOT
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    {['Not a CRM (no leads, deals, pipelines)', 'Not a chat app (use Slack for that)', 'Not HR software (no payroll, attendance)', 'Not project management (no Gantt charts)', 'Not AI hype (practical ops focus)'].map((item, i) => (
                      <motion.li key={i} className="flex items-center gap-3" initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} viewport={{ once: true }}>
                        ❌ {item}
                      </motion.li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </section>

        {/* How It Works - Horizontal steps */}
        <section id="how-it-works" className="py-24 relative">
          <div className="absolute inset-0 gradient-glow pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4 gap-2" variant="outline">
                <Workflow className="h-3 w-3" /> Simple Setup
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Up and running in minutes</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">Four simple steps from signup to full operational clarity</p>
            </motion.div>

            <motion.div 
              className="grid gap-6 md:grid-cols-4 max-w-5xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {workflowSteps.map((step, i) => (
                <motion.div key={i} variants={itemVariants} className="relative">
                  {i < 3 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px border-t-2 border-dashed border-primary/20" />
                  )}
                  <Card className="glass-card text-center p-6 relative z-10 hover:shadow-glow-sm transition-all duration-300 hover:-translate-y-2 group">
                    <div className="text-xs font-mono text-primary/60 mb-3 tracking-widest">{step.step}</div>
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4 group-hover:shadow-glow-sm transition-shadow">
                      <step.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Demo / Command Center */}
        <section id="demo" className="py-24">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4" variant="outline">
                <Award className="h-3 w-3 mr-1" /> Product Demo
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Your Operations Command Center</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">A unified dashboard for your entire operations team</p>
            </motion.div>

            <motion.div 
              className="max-w-5xl mx-auto"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
            >
              <Card className="overflow-hidden border border-border/40">
                <div className="aspect-video bg-gradient-to-br from-primary/5 via-background to-muted/30 flex items-center justify-center relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-background/20" />
                  
                  <div className="relative z-10 text-center p-8">
                    <motion.div 
                      className="w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-8"
                      animate={{ rotate: [0, 5, 0, -5, 0] }}
                      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Sparkles className="h-10 w-10 text-primary" />
                    </motion.div>
                    <h3 className="text-2xl md:text-3xl font-bold mb-4">See Everything. Control Everything.</h3>
                    <p className="text-muted-foreground max-w-lg mx-auto mb-8">
                      Tasks, approvals, workload, analytics — all in one place with real-time updates.
                    </p>
                    <Link to="/auth">
                      <Button size="lg" className="gap-2 shadow-glow-sm">
                        Try It Free <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>

                  {/* Floating feature badges */}
                  {[
                    { label: 'Real-time updates', icon: '🟢', pos: 'top-6 left-6' },
                    { label: 'Multi-language', icon: '🌍', pos: 'top-6 right-6' },
                    { label: 'Enterprise security', icon: '🔒', pos: 'bottom-6 left-6' },
                    { label: 'AI-powered', icon: '🤖', pos: 'bottom-6 right-6' },
                  ].map((badge, i) => (
                    <motion.div
                      key={i}
                      className={`absolute ${badge.pos} glass rounded-xl px-3 py-2 text-xs flex items-center gap-2`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <span>{badge.icon}</span> {badge.label}
                    </motion.div>
                  ))}
                </div>
              </Card>

              {/* Feature mini-cards */}
              <motion.div 
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {[
                  { icon: LineChart, title: 'Real-time Analytics', desc: 'Track team productivity' },
                  { icon: Workflow, title: 'Kanban & List Views', desc: 'Organize your way' },
                  { icon: Bell, title: 'Smart Notifications', desc: 'Never miss deadlines' },
                  { icon: Globe, title: 'Multi-language', desc: 'Full RTL support' },
                ].map((f, i) => (
                  <motion.div key={i} variants={scaleInVariants}>
                    <Card className="p-4 text-center glass-card hover:-translate-y-1 transition-all duration-300">
                      <f.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                      <h4 className="font-medium text-sm">{f.title}</h4>
                      <p className="text-xs text-muted-foreground">{f.desc}</p>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Features - Bento grid style */}
        <section id="features" className="py-24 relative">
          <div className="absolute inset-0 gradient-mesh pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4 gap-2" variant="outline">
                <Layers className="h-3 w-3" /> Features
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything you need to run operations</h2>
              <p className="text-muted-foreground">Focused tools for teams that ship</p>
            </motion.div>

            <motion.div 
              className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {features.map((f, i) => (
                <motion.div key={i} variants={itemVariants}>
                  <Card className="h-full glass-card group hover:shadow-glow-sm transition-all duration-500 hover:-translate-y-2 overflow-hidden relative">
                    <div className={`absolute inset-0 bg-gradient-to-br ${f.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    <CardHeader className="relative z-10">
                      <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/10 flex items-center justify-center mb-3 group-hover:shadow-glow-sm transition-shadow">
                        <f.icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="flex items-center gap-2">
                        {f.title}
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="relative z-10">
                      <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Trust Signals - Horizontal marquee style */}
        <section className="py-20 border-y border-border/50">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Enterprise Ready from Day One</h2>
              <p className="text-muted-foreground text-sm">Security and compliance built into every layer</p>
            </motion.div>
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-5 gap-8 max-w-4xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                { icon: Shield, label: 'Row-Level Security' },
                { icon: Lock, label: 'Role-Based Access' },
                { icon: FileCheck, label: 'Audit Logs' },
                { icon: TrendingUp, label: 'Data Export' },
                { icon: Building2, label: 'Multi-tenant' },
              ].map((item, i) => (
                <motion.div key={i} variants={scaleInVariants} className="flex flex-col items-center gap-3 text-center group">
                  <div className="w-14 h-14 rounded-2xl bg-success/10 flex items-center justify-center group-hover:bg-success/20 group-hover:shadow-glow-sm transition-all duration-300">
                    <item.icon className="h-6 w-6 text-success" />
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-24">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4" variant="outline">
                <Clock className="h-3 w-3 mr-1" /> Limited Time: 14-Day Pro Trial Free
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.pricing.title')}</h2>
              <p className="text-muted-foreground">{t('landing.pricing.subtitle')}</p>
            </motion.div>

            <motion.div 
              className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {['starter', 'professional', 'enterprise'].map((plan, i) => (
                <motion.div key={plan} variants={itemVariants}>
                  <Card className={`h-full relative overflow-hidden transition-all duration-300 hover:-translate-y-2 ${i === 1 ? 'glow-border shadow-glow-sm' : 'glass-card'}`}>
                    {i === 1 && (
                      <div className="absolute top-0 left-0 right-0 h-1 gradient-primary" />
                    )}
                    {i === 1 && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="gradient-primary text-primary-foreground shadow-glow-sm">Most Popular</Badge>
                      </div>
                    )}
                    <CardHeader className="pt-8">
                      <CardTitle className="flex items-center gap-2">
                        {i === 0 && <Rocket className="h-5 w-5 text-muted-foreground" />}
                        {i === 1 && <Sparkles className="h-5 w-5 text-primary" />}
                        {i === 2 && <Building2 className="h-5 w-5 text-muted-foreground" />}
                        {t(`landing.pricing.${plan}.name`)}
                      </CardTitle>
                      <div className="text-4xl font-bold mt-2">
                        {t(`landing.pricing.${plan}.price`)}
                        <span className="text-sm font-normal text-muted-foreground">
                          {t(`landing.pricing.${plan}.period`)}
                        </span>
                      </div>
                      <CardDescription>{t(`landing.pricing.${plan}.description`)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-8">
                        {(t(`landing.pricing.${plan}.features`, { returnObjects: true }) as string[]).map((feature, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm">
                            <Check className="h-4 w-4 text-success flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Link to="/auth">
                        <Button className={`w-full ${i === 1 ? 'shadow-glow-sm' : ''}`} variant={i === 1 ? 'default' : 'outline'} size="lg">
                          {plan === 'enterprise' ? t('landing.pricing.contactSales') : t('landing.pricing.getStarted')}
                        </Button>
                      </Link>
                      {i === 1 && (
                        <p className="text-xs text-center text-muted-foreground mt-3">14-day free trial included</p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 relative">
          <div className="absolute inset-0 gradient-glow pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4" variant="outline">
                <Star className="h-3 w-3 mr-1 fill-warning text-warning" /> Customer Stories
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Trusted by operations teams worldwide</h2>
              <p className="text-muted-foreground">See what our customers are achieving with OpsGPT</p>
            </motion.div>

            <motion.div 
              className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {testimonials.map((testimonial, i) => (
                <motion.div key={i} variants={itemVariants}>
                  <Card className="h-full glass-card hover:-translate-y-2 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="flex gap-1 mb-4">
                        {[...Array(5)].map((_, j) => (
                          <Star key={j} className="h-4 w-4 fill-warning text-warning" />
                        ))}
                      </div>
                      <p className="mb-6 text-muted-foreground italic leading-relaxed">"{testimonial.quote}"</p>
                      <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-full gradient-primary flex items-center justify-center font-semibold text-primary-foreground text-sm">
                          {testimonial.avatar}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold">{testimonial.name}</div>
                          <div className="text-xs text-muted-foreground">{testimonial.role}</div>
                        </div>
                        <Badge variant="secondary" className="text-xs">{testimonial.metric}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            </motion.div>
            <motion.div 
              className="grid gap-4 md:grid-cols-2 max-w-4xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                { q: 'Is there a free plan?', a: 'Yes! Our Starter plan is free forever with essential features. You also get a 14-day Pro trial when you sign up.' },
                { q: 'Do I need the AI features?', a: 'No! OpsGPT works great without AI. AI features are optional intelligence boosters, not core requirements.' },
                { q: 'What languages are supported?', a: 'English, Urdu, Hindi, and Arabic with full RTL support. More languages coming soon.' },
                { q: 'Is my data secure?', a: 'Absolutely. We use row-level security, role-based access, and full audit logs. Enterprise-grade from day one.' },
              ].map((faq, i) => (
                <motion.div key={i} variants={itemVariants}>
                  <Card className="glass-card hover:border-primary/30 transition-colors">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary font-mono">{i + 1}</span>
                        {faq.q}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">{faq.a}</CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Changelog / Updates - SEO content section */}
        <section id="changelog" className="py-24 relative">
          <div className="absolute inset-0 gradient-mesh pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4 gap-2" variant="outline">
                <Rocket className="h-3 w-3" /> What's New
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Changelog & Updates</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">We ship fast. Here's what's new in OpsGPT.</p>
            </motion.div>

            <motion.div 
              className="max-w-3xl mx-auto space-y-6"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {[
                {
                  version: 'v1.4.0',
                  date: 'March 2, 2026',
                  tag: 'Latest',
                  tagColor: 'bg-success/20 text-success',
                  items: [
                    { type: '✨', text: 'Privacy Policy & Terms of Service pages with full SEO' },
                    { type: '💬', text: 'Crisp live chat integration on all public pages' },
                    { type: '📧', text: 'Contact form with email delivery via backend functions' },
                    { type: '🎨', text: 'Completely redesigned landing page with premium animations' },
                  ]
                },
                {
                  version: 'v1.3.0',
                  date: 'February 20, 2026',
                  tag: 'Feature',
                  tagColor: 'bg-primary/20 text-primary',
                  items: [
                    { type: '🤖', text: 'AI-powered task suggestions and risk alerts' },
                    { type: '📊', text: 'Advanced analytics dashboard with export capabilities' },
                    { type: '🔔', text: 'Smart notification system with customizable preferences' },
                    { type: '🌍', text: 'Full RTL support for Arabic and Urdu languages' },
                  ]
                },
                {
                  version: 'v1.2.0',
                  date: 'February 5, 2026',
                  tag: 'Improvement',
                  tagColor: 'bg-info/20 text-info',
                  items: [
                    { type: '⚡', text: 'Kanban board with drag-and-drop task management' },
                    { type: '👥', text: 'Team workload balancing and capacity planning' },
                    { type: '✅', text: 'Multi-level approval workflows with audit trail' },
                    { type: '🔐', text: 'Row-level security and role-based access control' },
                  ]
                },
                {
                  version: 'v1.0.0',
                  date: 'January 15, 2026',
                  tag: 'Launch',
                  tagColor: 'bg-warning/20 text-warning',
                  items: [
                    { type: '🚀', text: 'Initial public beta release of OpsGPT' },
                    { type: '📋', text: 'Core task management with templates and time tracking' },
                    { type: '👤', text: 'User authentication and organization management' },
                    { type: '🎯', text: 'Operations dashboard with real-time updates' },
                  ]
                },
              ].map((release, i) => (
                <motion.div key={i} variants={itemVariants}>
                  <Card className="glass-card overflow-hidden hover:border-primary/30 transition-colors group">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-lg font-bold text-primary">{release.version}</span>
                          <Badge className={`text-[10px] uppercase tracking-wider ${release.tagColor}`}>
                            {release.tag}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">{release.date}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2">
                        {release.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-3 text-sm text-muted-foreground">
                            <span className="text-base leading-5 flex-shrink-0">{item.type}</span>
                            <span>{item.text}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
            >
              <Card className="bg-primary text-primary-foreground text-center p-10 md:p-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
                <div className="absolute inset-0 opacity-[0.04]" style={{
                  backgroundImage: `radial-gradient(circle, hsl(0 0% 100%) 1px, transparent 1px)`,
                  backgroundSize: '24px 24px'
                }} />
                <div className="relative z-10">
                  <div className="w-14 h-14 mx-auto mb-8 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
                    <Sparkles className="h-7 w-7 text-primary-foreground/80" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to streamline your operations?</h2>
                  <p className="mb-2 text-primary-foreground/90 text-lg">Start your free 14-day Pro trial today. No credit card required.</p>
                  <p className="mb-8 text-sm text-primary-foreground/70">Works with or without AI — you're in control.</p>
                  <Link to="/auth">
                    <Button size="lg" variant="secondary" className="gap-2 text-base px-8 h-12 shadow-xl hover:scale-105 transition-transform">
                      Start Free Trial <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <div className="flex justify-center gap-6 mt-8 text-sm text-primary-foreground/60">
                    <span className="flex items-center gap-1"><Check className="h-4 w-4" /> Free 14-day trial</span>
                    <span className="flex items-center gap-1"><Check className="h-4 w-4" /> No credit card</span>
                    <span className="flex items-center gap-1"><Check className="h-4 w-4" /> Cancel anytime</span>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-16 bg-background/50">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-4 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-primary-foreground" />
                </div>
                <span className="font-bold text-lg">OpsGPT</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">AI-powered operations control system for modern teams.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a></li>
                <li><a href="#demo" className="text-muted-foreground hover:text-foreground transition-colors">Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/support" className="text-muted-foreground hover:text-foreground transition-colors">Support</Link></li>
                <li><Link to="/auth" className="text-muted-foreground hover:text-foreground transition-colors">Sign In</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} OpsGPT. All rights reserved.</p>
            <p className="text-xs text-muted-foreground">Business Operations Control System</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
